import { Translations } from "./types";

export default {
    common: {
        close: "close",
        yes: "yes",
        no: "no",
        or: "or",
        loading: "Loading",
    },
    bookingFlow: {
        stepNavAriaLabel: "Booking progress",
        steps: {
            lab: "Lab",
            consultation: "Consultation",
            clinic: "Clinic",
            form: "Form",
            pay: "Reserve",
            confirmation: "Confirmation",
        },
        screeningIndicator: {
            stbbiScreening: "STBBI Screening",
            cervicalCancer: "Cervical Cancer Screening",
            change: "Change",
        },
        screeningSwitch: {
            title: "Start a new screening?",
            body:
                "Switching to <strong>{serviceName}</strong> starts a brand new booking flow. You'll lose the lab, consultation and clinic choices you've made for this screening.",
            confirm: "Start new screening",
            cancel: "Cancel",
        },
        labSelection: {
            title: "Choose your lab",
            subtitle:
                "Either lab below provides the same requisition and tests. This choice determines how the lab is processed and how fast your results come back.",
            publicEyebrow: "Public — RAMQ network",
            publicHeadline: "Within 10 days",
            publicPriceChip: "$5 to $70",
            publicDescription:
                "Analyzed at a public lab. A valid RAMQ card is required. <strong>RAMQ covers the analysis — it does not cover the transport fee</strong>, paid at the clinic.",
            privateEyebrow: "Private — Priority Lab",
            privateHeadline: "As little as 24h",
            privatePriceChip: "$225 (on average)",
            privateBenefitOne:
                "Priority lab processing — same day results possible",
            privateBenefitTwo:
                "Express results shared online in Prelib's Patient Platform",
            privateBenefitThree:
                "No RAMQ card needed. Lab fee <strong>~$225</strong> (often reimbursed by private insurance)",
            footnote:
                "1 : Monday to Friday appointments, timelines vary with collection time and lab operations. They are not guaranteed.",
            nextButton: "Continue",
        },
        hpvService: {
            continueButton: "Continue",
        },
        consultation: {
            receiveResultsTitle: "Results follow-up: online or by phone?",
            receiveResultsSubtitle:
                "This choice determines how you receive your results and who follows up on them. It does not change your tests or your prescription.",
            explainsResultsTitle: "Who explains your results?",
            explainsResultsSubtitle:
                "This is only about how you're contacted — it doesn't change your test panel or your prescription.",
            phoneCallTitle: "By phone",
            phoneCallResultsChip: "Results within 10 days",
            phoneCallPriceChip: "$5 to $15",
            phoneCallBody:
                "A phone appointment with a member of the medical team must be scheduled to complete your episode of care, including the management of your results, positive or negative",
            expressOnlineTitle: "Express online",
            expressResultsChip: "Results in 5 days",
            expressPriceChip: "$60 to $70",
            expressLead: "Personalized care by a dedicated nurse",
            expressBody:
                "As soon as your results come in, a nurse interprets them, shares them in the patient portal and handles your follow-up. <strong>No phone call*</strong>, unless a result requires action.",
            feeSummaryRegular: "$5 to $15 — transport fee only.",
            feeSummaryExpress: "$55 nursing fee + $5–15 transportation fee.",
            continueToClinic: "Continue",
        },
        payment: {
            dueNow: "Due now",
            dueAtClinic: "Due at the clinic",
            paymentMethod: "Payment method",
            guaranteeCardSection: "Guarantee card",
            orPayByCard: "or pay by card",
            loadingPaymentMethods: "Loading payment options…",
            walletPaymentDescription: "Prelib screening appointment",
            walletNoShowLabel: "Cancellation or late-change fee",
            acknowledgment:
                "I understand that <strong>{clinicAmount} is due at the clinic</strong>, payable in cash, debit or credit, and that <strong>no-show and late cancellation fees apply</strong>: cancel or reschedule for free online up to 4 hours before, after that or if I don't show up, a $35 fee is charged.",
            cardOnFileAcknowledgment:
                "The credit card information provided above will only be used to charge a <strong>$35 fee plus taxes in the event of a no-show or late cancellation</strong> (less than 4 hours before the appointment). Cancellations and rescheduling are free online up to 4 hours before. I understand that <strong>$5 to $15 is due at the clinic</strong>, payable by cash, debit or credit.",
            noChargeTodayFooter: "No amount will be charged today.",
            reservationSubtitle: {
                confirm: "Confirm your reservation on",
                at: "at",
            },
            reservationSubtitleFallback: "Confirm your reservation.",
            cashAcknowledgmentModalTitle: "Cash payment",
            cashAcknowledgmentModalBody:
                "When paying in cash at the clinic, a <strong>$35 fee (plus taxes)</strong> still applies for missed appointments or cancellations made less than 4 hours in advance. This fee must be paid before another appointment can be booked.",
            cashAcknowledgmentConfirm: "I consent",
            cashAcknowledgmentRefuse: "I refuse",
        },
    },
    labSelectionPage: {
        title: "Desired turnaround time for results",
        footnote: {
            publicLabDelay:
                "1 : Timelines not guaranteed. Based on average business days.",
            insurancePlansReimbursement: "2 : Unless results are positive.",
        },
        finalPriceNote: "Final price to be calculated at checkout.",
        button: "Next",
    },
    hpvServicePage: {
        title: "Cervical cancer",
        description:
            "Private pathway beginning with a vaginal HPV self-test. The fee includes the consultation and the prescription issued on site.",
        details: {
            title: "HPV Screening",
            info1: "Results delivered online ¹",
            info2: "Reimbursed by most private insurance plans",
            footnote:
                "1: Except in the case of positive results, where a gynecological exam (with Pap test) will be required.",
            price: "$199",
            priceNote: "(consultation + analysis)",
        },
        button: "Next",
    },
    availabilityPublicPage: {
        title: "Select a clinic",
        resultsTurnaroundTime: "Results turnaround time",
        description: {
            postalCode: "The fastest near ",
            geolocation: "The fastest near ",
            you: "you",
            enterYourLocation: "Set your location",
            closestToYou: "to find the closest clinics near you.",
            resultsDelayFor: "For ",
            resultsDelayPublicLab: "results in 10 days",
            resultsDelayPrivateLab: "results the next day",
        },
        findClinic1: "Find a clinic",
        findClinic2: "close to you!",
        useMyLocation: "use my location",
        useMyPostalCode: "use my postal code",
        location: "Location",
        postalCode: "Postal Code",
        yourLocation: "Your location",
        search: "Search",
        ramqPopover: {
            ramqPopoverTitlePrefix: "The service",
            ramqPopoverTitleEmphasis: "within 10 days",
            ramqPopoverTitleSuffix: "is only available with a valid RAMQ card",
            ramqPopoverCtaPrefix: "Switch to",
            ramqPopoverCtaEmphasis: "as soon as the next day",
        },
        locationAccessError:
            "Location access was denied or unavailable. Please enter your postal code.",
        publicLabOptionTitle: "Within 10 days",
        publicLabOptionBenefitOne: "Results communicated by phone",
        publicLabOptionBenefitTwo: "A valid RAMQ card is required",
        publicLabOptionPrice: "5$ to 15$",
        publicLabOptionPriceNote: "(sample transportation costs)",
        privateLabOptionTitle: "As soon as the next day",
        privateLabOptionBenefitOne: "Results shared online",
        privateLabOptionBenefitTwo:
            "Reimbursed by most private insurance plans",
        privateLabOptionPriceRange: "225$",
        privateLabOptionPriceNote:
            "(on average depending on the selected tests)",
        turnaroundFilter: {
            public: {
                option: "Within 10 days",
                price: "5$ to 15$",
            },
            private: {
                option: "As soon as the next day",
                price: "Around $225",
            },
        },
        screeningType: "Screening type",
        yourCity: "Your city",
        ramqValidLabel: "I have a valid RAMQ card",
    },
    appointmentConfirmation: {
        title: "Your appointment is confirmed. Yay!",
        subtitle:
            "An email with the details has been sent to you. If you have any questions, feel free to write to us at ",
        reminders: {
            title: "Don't forget!",
            bringRAMQ: {
                main: "Bring your RAMQ card",
                note:
                    " and a payment method. No RAMQ? Bring a photo ID instead.",
            },
            beOnTime: { main: "Be exactly on time!" },
            cancelEarly: {
                main: "Cancel at least 4 hours in advance",
                note: " to avoid a $35 fee.",
            },
        },
        icsFileTitle: "Prelib Appointment",
        serviceDescription: {
            regular:
                "Phone follow-ups are handled by the doctors at Clinique médicale en santé sexuelle inc.",
            express:
                "Express follow-ups are handled by the nurses at Centre de prévention en santé sexuelle inc.",
        },
        fastestNearPostalCode: "The fastest near ",
        phoneAppointmentLocation: "Phone appointment",
    },
    appointmentsSection: {
        upcomingAppointments: "Your upcoming appointments",
        pastAppointments: "Your past appointments",
        noAppointment: "You have no appointment yet",
        noAppointmentYet: "You don't have any appointments yet.",
        clickHereToSchedule: "Click here to get a screening.",
        appointmentCard: {
            cancelButtonText: "Cancel",
            editButtonText: "Modify",
            rescheduleButton: "Reschedule",
            retakeQuestionnaire: "Fill out the questionnaire again",
            payMissedAppointmentButtonText: "Pay missed appointment",
            cancellationIndicator: "Cancelled",
            staffCancellationIndicator: "Must be rescheduled",
            consultButtonText: "Consult",
            dateIndicator: (date) => `${date} screening appointment `,
            medicalTestsIndicator: "Tests required",
            resultsIndicator: "Your results",
            missed: "Missed",
            missedMessage:
                "You’ve missed your appointment, but you still have time to reschedule it.",
            resultsNotReadyLine1: "We’re still waiting on your results;",
            resultsNotReadyLine2: "it shouldn’t take much longer.",
            missedAppointmentMessage:
                "It looks like you’ve missed your appointment and your medical questionnaire has expired. To schedule another appointment, fill out the online medical questionnaire once again.",
        },
        phoneAppointmentCard: {
            chooseButtonText: "Choose",
            editButtonText: "Change",
            consultButtonText: "Consult",
            rejectButtonText: "Decline",
            title: "Phone appointment",
            independantTitle: "Phone appointment",
            prepTitle: "PrEP phone appointment",
            rejectedIndicator: "Declined",
            canceledIndicator: "Canceled",
            dateIndicator: (date) => `${date} phone appointment`,
            prepDateIndicator: (date) => `PrEP phone appointment of ${date}`,
            independantDateIndicator: (date) => `${date} phone appointment`,
            descriptionLine1: (assAppDate) =>
                `Phone appointment concerning your ${assAppDate} screening appointment.`,
            descriptionLine2:
                "One of our staff members will call you at the time of your appointment.",
            prepDescription:
                "The call with the participating pharmacy provides all the information needed to determine whether or not PrEP can be initiated, and to ensure the necessary follow-up.",
            prepDescription2:
                "If the phone appointment is not completed or if you are not eligible, the creatinine sample will be cancelled.",
            missedMessage:
                "It looks like you've missed your phone appointment, but you can still reschedule it.",
            chooseDate:
                "You’ve been invited to a phone appointment. Choose a time and date.",
        },
        scheduleModal: {
            confirmButtonText: "Confirm",
            exitButtonText: "Cancel",
            payButtonText: "Pay",
        },
        cancellationModal: {
            cancellationButtonText: "Cancel appointment",
            header: (date) => `Modify your ${date} appointment`,
            rescheduleButtonText: "Choose another date*",
            feesMightApply: "*Certain fees may apply.",
            faqUrl: "https://www.prelib.com/en?faq=14#faqs",
            phoneAppointmentRejected: "Your appointment was declined.",
            cancelAppointmentNotAllowed:
                "It is not allowed to cancel a missed appointment or an appointment taking place in less than 4 hours.",
        },
        cancellationConfirmModal: {
            title: "Are you sure you want to cancel your appointment?",
            phoneTitle:
                "Are you sure you want to cancel your phone appointment?",
            prepEligibility:
                "If you cancel your call with the pharmacist, PrEP-related services cannot be provided. You will still be able to go to your STI screening appointment.",
            content:
                "Please, note that the fees for the online medical evaluation are non-refundable.",
            confirmButtonText: "Yes, cancel appointment",
            exitButtonText: "No, keep my appointment",
            appointmentCancelled: "Your appointment was cancelled.",
        },
        creditInfoModal: {
            cardBillingAddressLabel: "Billing address",
            cardExpirationLabel: "Expiration date",
            cardHolderNameLabel: "Cardholder name",
            content:
                "Please confirm your credit card information for the payment of\u00A0",
            modifyButton: "Modify",
            link: "the $35 administrative fee (taxes not included).",
            faqUrl: "https://www.prelib.com/en?faq=14#faqs",
            payButton: "Pay",
        },
        gatherCreditInfoModal: {
            content:
                "Please enter your credit card information for the payment of \u00A0",
            link: " the $35 administrative fee (taxes not included).",
            walletIntro:
                "Pay the $35 administrative fee (taxes not included) with Apple Pay, Google Pay, or a card.",
        },
        rescheduleAppointmentModal: {
            title: "Select a date",
            prepSubtitle:
                "The call with the pharmacist must precede your appointment for the",
            confirmButtonText: "Select",
            exitButtonText: "Cancel",
        },
        feedbackModal: {
            title: "Satisfaction Survey",
            letUsKnowHowItWent: "Tell us about your experience at Prelib.",
            satisfactionLevel:
                "What is your level of satisfaction with the Prelib service?",
            recommendationProbability:
                "How likely are you to recommend Prelib to your friends or colleagues?",
            recommendationJustification:
                "In your own words, why did you provide this rating?",
            textAreaPlaceholder: "Please write a note ...",
            phoneCommunicationAgreement:
                "Would you be comfortable if a member of our team contacted you by phone in order to improve our services?",
            yes: "Yes",
            no: "No",
            confirmButtonText: "Submit",
            exitButtonText: "Cancel",
            notifications: {
                satisfactionQuestionnaireSentSuccess:
                    "Satisfaction survey sent.",
                satisfactionQuestionnaireSentError:
                    "Error when sending the satisfaction survey.",
            },
        },
        googleReviewModal: {
            title: "Amazing!",
            subtitle: "Share the love on Google",
            reviews: [
                "Staff was so quick, helpful and friendly to fix the issue.",
                "My experience was perfect.",
            ],
            reviewButtonText: "Rate and Review",
            postponeButtonText: "Ask me later",
            refuseButtonText: "No, thanks",
            notifications: {
                publicReviewPreferenceSaveSuccess: "Preference saved.",
                publicReviewPreferenceSaveError:
                    "Error : your preference could not be saved.",
            },
        },
    },
    dateSection: {
        titleWithPrep: "Make an appointment",
        prepSubtitle: "PrEP Solution",
        prepDescription:
            "The PrEP Solution program makes it easy for you to get PrEP. To benefit from this service, a phone appointment with a participating pharmacist is required to check your eligibility. If you don't want to take PrEP through the PrEP Solution program, you can still book an appointment for an STI screening without any problems, and then get PrEP through the medical clinic and pharmacy of your choice.",
        prepConsent:
            "*I agree that a pharmacy participating in the PrEP Solution program will receive my contact information and the information relevant to my prescription and will contact me as available.",
        makePrepAppointmentButtonText: "Make an appointment",
        abortPrepCheckboxText: "No, I don't want PrEP",
        prepNotDecidedError:
            "Please indicate if you are interested in following PrEP with us",
        screeningSectionTitle: "Screening",
        screeningSectionDescription:
            "It is important that the screening appointment takes place after the telephone appointment with a participating pharmacist.",
        clinicsAreLoading: "List of screening clinics currently being uploaded",
        pharmacistCalendarIsLoading: "Pharmacist calendar is loading",
        header: "Select your clinic and time slot.",
        submitButtonText: "Submit",
    },
    dateSelectorComponent: {
        choseDateLabel: "Select a date",
        choseHourLabel: "Time",
        noAvailability: "No availability",
        noDateAvailability:
            "No availability at the moment. Please try again tomorrow.",
        scrollTimesHint: "Swipe to see all times",
    },
    unsubscribeConfirmation: {
        goToHomeButton: "View your profile",
        header: "Unsubscribing successful",
        invalidTokenError: "Invalid or expired",
        successMessage:
            "You have successfully unsubscribed from all automated email communications. You can always resubscribe by clicking on “Subscribe to follow-up emails” in the “Personal Information” tab of your patient portal.",
    },
    expiredSection: {
        header: "Your questionnaire has expired.",
        restart: "Fill out the questionnaire once more",
        message:
            "Your questionnaire has expired.\n\nThe 45-day deadline for making an appointment has passed. Your medical questionnaire is therefore no longer valid, and a complete re-evaluation is necessary.\n\nIf you have any questions, contact us at info@prelib.com, and a member of our team will get back to you shortly.",
    },
    emailConfirmation: {
        goToHomeButton: "View your profile",
        header: "Email address confirmed",
        invalidTokenError: "Invalid or expired",
        redirectMessage: "Automatic redirect to booking in",
        redirectMessageSeconds: "second[s]",
    },
    footer: {
        needHelp: "Need help?",
        termsAndConditions: "Terms and conditions",
        termsAndConditionsLink: "https://prelib.com/en/terms/",
        privacyPolicy: "Privacy policy",
        privacyPolicyLink: "https://prelib.com/en/privacy-policy/",
    },
    forgottenPassword: {
        header: "Password forgotten",
        instruction:
            "Enter your email address below and we’ll send you an email to reset your password.",
        rhetoricalQuestion: "Forgot your password\u00A0?",
        emailLabel: "Email address",
        invalidEmailError: "Invalid email address",
        sendButtonText: "Submit",
        successMessage: "Thank you!",
        rateLimitWaitMessage:
            "Please wait before requesting another password reset email.",
    },
    homePage: {
        loadingInfo: "Loading your info",
        title: "Which screening would you like to do?",
        topMenu: {
            completeScreeningButtonText: "Confirm the appointment",
            disconnectButtonText: "Sign out",
            greetingsText: "Hi",
            makeScreeningButtonText: "Get tested",
            updateProfile: "Update my profile",
        },
        subheaderTab: {
            screeningTitle: "Carry out a screening",
            myAppointmentTitle: "My appointments",
            myPrepTitle: "My PrEP space",
            hpv: {
                badge: "New",
                title: "Cervical Cancer",
                subtitle: "HPV self-test",
            },
            sti: {
                title: "STBBI",
                subtitle: "Complete screening",
            },
            prepTitle: "PrEP space",
        },
        tooltipTab: {
            screening: "You already have an appointment.",
            phoneAppointment:
                "You must book and complete your follow-up phone appointment before you can carry out a new screening.",
            retake:
                "You must retake your last test before booking a new one. See below.",
        },
        payMissedAppointmentFeesPopup: {
            title:
                "Because you missed your last appointment, you must pay 35$ before you can book a new appointment.",
        },
        bookedScreeningPopup: {
            title: "You already have an appointment",
            body: (screeningLabel, date, time, clinic) =>
                `Your ${screeningLabel} screening on ${date} at ${time}, at ${clinic}, is already booked.`,
            cta: "View the appointment",
            reservedStatus: "Booked",
        },
    },
    infoConfirmationSection: {
        birthDateLabel: "Select your birth date!",
        birthDateConfirmation: "Confirm date of birth",
        header: "Personal information",
        completeButtonText: "Fill out",
        confirmInfo: "Confirm information",
    },
    login: {
        alreadyHasAccountLink: "You already have an account.",
        createAccountLink: "Create an account",
        emailConfirmationMessage: "You must now confirm your email address.",
        emailLabel: "Email address",
        firstNameLabel: "First name",
        firstNameWarning: "As shown on your RAMQ health insurance card.",
        forgottenPasswordLink: "Forgot your password?",
        invalidEmailError: "Invalid email address",
        invalidPasswordError: "Invalid password",
        loginHeader: "My account",
        lastNameLabel: "Last name",
        lastNameWarning: "As appears on RAMQ health insurance card",
        loginButtonText: "Sign in",
        consentMessage: `I have read and accept Prelib's <a href="https://prelib.com/en/terms" rel="noopener noreferrer" target="_blank">general terms of use </a> as well as <a href="https://prelib.com/confidentiality" rel="noopener noreferrer" target="_blank"> its privacy policy </a> .`,
        passwordErrors: {
            maxLength:
                "Your password must contain no more than 128 characters.",
            minLength: "Your password must contain at least 10 characters.",
            needLowercase:
                "Your password must contain at least one lower-case letter.",
            needNumber: "Your password must contain at least one number.",
            needSpecialSymbol:
                "Your password must contain at least one non-alphanumeric symbol.",
            needUppercase:
                "Your password must contain at least one capital letter.",
        },
        passwordWarning:
            "Your password is strictly confidential and must not be disclosed to third parties.",
        passwordLabel: "Password",
        tfaLabel: "2-Step Verification",
        tfaDisclaimer:
            "For the security of your data, your account will now be protected by 2-step authentication. When logging in, you will have to confirm your identity using a 6-digit code. How do you want to receive this code?</b>",
        byEmail: "By email",
        bySms: "By SMS",
        phoneNumberLabel: "Phone number",
        phoneNumberPlaceholder: "(xxx) xxx-xxxx",
        phoneNumberError: "Invalid phone number",
        requiredConsentError: "Consent required",
        requiredFieldError: "Required field",
        signUpButtonText: "I am registering",
        signUpHeader: "Create my Prelib file",
        resendConfirmationEmail: "Resend the confirmation email to ",
        confirmationResent: "A confirmation email was just sent to ",
        accountCreated: "Your file was created",
        resend: "Resend confirmation email",
        formErrors: "There are errors in the form",
        hotmailWarning:
            "If you use @hotmail, Prelib emails may end up in your spam folder. We recommend that you add Prelib to your contact list so you don't miss any emails.",
        warning: "Warning",
    },
    notification: {
        createdAppointment: "Your appointment has been scheduled",
        canceledAppointment: "Appointment cancelled!",
        rescheduledAppointment: "Appointment rescheduled!",
        scheduledPhoneAppointment: "Phone appointment scheduled",
        appointmentUpdated: "Your appointment was updated",
        retakeRejectedAppointment: "Appointment rescheduling declined",
        unexpectedError: "An error has occurred!",
    },
    upfrontPaymentSection: {
        public: {
            regularServiceLabel: "In 10 days*",
            regularServiceDescription:
                "Results by <strong>telephone</strong>, with a doctor.",
            expressServiceLabel: "In 5 days - $55*",
            expressServiceDescription:
                "Results by <strong>email</strong>, with follow-up by a nurse clinician.",
            transportFeeMessage: "*Transport fee to pay at the clinic:",
            transportFeeAmount: "$5 to $15",
            expressTransportFeeAmount: "$15",
            cashPaymentInClinic: "Cash payment in clinic",
        },
        private: {
            serviceDelay: "As soon as the next day - $55*",
            serviceDelivery:
                "Results by <strong>email</strong>, with follow-up by a nurse clinician.",
            labFeeMessage: "Private lab fee to pay at the clinic:",
            labFeeAmount: "$150 to $375",
        },
        hpv: {
            title: "Confirmation and payment",
            description: {
                confirm: "Confirm your appointment on",
                at: "at",
            },
            descriptionFallback: "Confirm your appointment.",
            now: "Now",
            consultationText: "Consultation + Requesition",
            consultationPrice: "$55",
            inClinic: "At the clinic",
            analysisText: "Lab analysis",
            analysisPrice: "$144",
            confirmButtonText: "Confirm and pay $55",
            cancellationOrReschedulingFeeWarning:
                "Cancellation or change fee within 4 h of the appointment: $35.",
        },
        sectionTitle: "Medical support",
        alternateSectionTitle: "Reservation confirmation",
        description: {
            confirm: "Confirm your appointment on",
            at: "at",
        },
        descriptionFallback: "Confirm your appointment.",
        resultsTurnaroundTimeHeading: "Results turnaround time",
        followUpChoices: {
            legend: "Select the kind of follow-up provided by our team.",
            noRamqExplanation:
                "This type of follow-up allows you access to a screening without a RAMQ card.",
            regular: {
                title: "Regular — No charge",
                description: [
                    "Results within <strong>10 days</strong> on <em>average</em>*",
                    "Results by <strong>phone</strong>",
                ],
            },
            express: {
                title: "Express — 55$",
                description: [
                    "Results within <strong>5 days</strong> on <em>average</em>*",
                    "Results by <strong>email</strong> (unless positive)",
                ],
            },
        },
        cancellationOrReschedulingFeeWarning:
            "Cancellation or rescheduling fee if less than 4 hours before the appointment: $35",
        chargeWarning:
            "A $35 fee applies if you reschedule your appointment less than 4 hours in advance.",
        delayWarning: "Delays are not guaranteed.",
        timeEstimateWarning: "Deadlines may vary",
        transportFeeWarning: "Transport fee to pay at the clinic: $5 to $15",
        paymentForm: {
            cardCvc: "CVC",
            cardExpiry: "MM/YY",
            cardNumber: "Card Number",
            payButtonPrefix: "Pay",
            noChargeButtonText: "Continue without charges",
            confirmButtonText: "Confirm",
            paymentOptionCash: "Pay with cash",
            edit: "Edit",
            walletOrCard: "Or pay with card",
            walletPaymentDescription: "Prelib Express",
            walletNoShowLabel: "Cancellation or late-change fee",
            walletUnavailable:
                "Apple Pay / Google Pay is unavailable on this browser or domain. Please use a card, or test on staging over HTTPS with the domain registered in Stripe.",
            walletProcessing: "Processing payment…",
        },
        missingRamqWarning: {
            question: "Want to skip the fee?",
            description: "Enter your RAMQ card.",
        },
    },
    paymentSection: {
        cancelButton: "Cancel",
        cardExpirationDate: "Expiration date (MM/YY)",
        cardHolderAddress: "Address",
        cardHolderCity: "City",
        cardHolderName: "Cardholder name",
        cardHolderZipCode: "Postal code",
        cardNumber: "Card number",
        cardSecurityCode: "Security code",
        chargeExplanation:
            "By completing this transaction, you’ll gain access to personalized recommendations and will be able to book your appointment.",
        header: "Payment",
        noRefundWarning:
            "Your card will be charged with a $\u00A041.50 non-refundable electronic medical evaluation fee.",
        payButtonText: "Pay",
        privacyMessage:
            "Your data is protected by the SSL standard Internet security protocol. The data is encrypted and exchanged with an HTTPS server. We respect your confidentiality. We provide your name, address and email address only to the merchant.",
        applyCouponLabel: "Apply promo code",
        applyCouponButton: "Apply",
        couponDiscountLabel: "Discount",
        delayDisclaimer:
            "* It may take some time to process the payment. Leave the page open until confirmation.",
        chargeErrorNotification:
            "Looks like something went wrong. No worries, double-check your card details and try again!",
    },
    profileSection: {
        addressLabel: "Address",
        addressPlaceHolder: "Address",
        birthDateLabel: "Date of birth",
        bornGender: "Gender assigned at birth",
        cancelTextButton: "Cancel",
        cardBillingAddressLabel: "Billing address",
        cardExpirationLabel: "Expiration date",
        cardHolderNameLabel: "Cardholder name",
        cityPlaceHolder: "City",
        emailRemindersDisabled: "Unsubscribe from follow-up emails",
        false: "No",
        firstNameLabel: "First name",
        generalHealthHeader: "General Health",
        jobLabel: "Occupation",
        lastNameLabel: "Last Name",
        modifyLabel: "Change",
        modifyTextButton: "Change",
        paymentMethodHeader: "Payment method",
        personalInformationHeader: "Personal information",
        ramqNumberExpirationLabel: "RAMQ expiration",
        ramqNumberLabel: "RAMQ number",
        ramqNumberError: "Invalid RAMQ number",
        phoneNumberLabel: "Phone number",
        phoneNumberError: "Invalid phone number",
        selfDefinedGender: "Gender",
        sexualHealthHeader: "Sexual health",
        choose: "Select",
        true: "Yes",
        zipCodePlaceHolder: "Postal code",
        followUpEmails: "Follow-up emails",
        unsubscribe: "Unsubscribe",
        subscribe: "Subscribe",
        confirmPhoneNumber: "Confirm your phone number",
        tfaPreference: "2-Step authentication preferences",
        phoneNumberMustBeConfirmed:
            "You must confirm your phone number in order to use text messaging as your 2-Step authentication method.",
        cancel: "Cancel",
        save: "Save",
        password: "Password",
        requestPassword: "Send me a password reset link",
        sending: "Sending...",
        emailSent: "Email sent",
        ramqRequiredModal: {
            attentionLabel: "Attention!",
            title: "A RAMQ card is required",
            description:
                "You selected the public laboratory service, reserved for holders of a valid RAMQ card.",
            primaryAction: "Enter my RAMQ card",
            secondaryAction: "Choose private service",
            secondaryActionHelper: "Without RAMQ — fees apply",
        },
    },
    appointmentHoldBanner: {
        message: "remaining to confirm your appointment",
    },
    appointmentHoldExpiredModal: {
        title: "Your reservation has expired",
        description:
            "The 30-minute hold on your appointment time has ended. To continue booking, choose a new date and time.",
        cta: "Restart appointment",
    },
    phoneConfirmationModal: {
        title: "Phone number confirmation",
        sentTo: (phone) =>
            `A confirmation code has been sent to ${phone} via SMS.`,
        instructions:
            "Enter your confirmation code in the field below to confirm your phone number.",
        notReceived: "Haven’t received your code yet?",
        resend: "Send another code",
        wrongPhone: "Wrong phone number?",
        changePhone: "Click here to update your profile",
        placeholder: "6-digit code",
        submit: "Confirm",
        invalid: "Invalid code",
        successMessage: "Your phone number is confirmed",
    },
    appointmentNotAvailableModal: {
        title: "This time slot is no longer available",
        overline: "Oopsie doopsie!",
        description:
            "The reservation expired or someone else just booked this time slot. It happens!",
        cta: "Select date and time",
    },
    appointmentSummaryModal: {
        title: "Reserved for 30 minutes",
        description: "Complete your registration to confirm your appointment.",
        cta: "Continue",
    },
    questionnaireComponent: {
        blockText: "or press <strong>Enter</strong>",
        incompleteQuestionnaireError: "Invalid form",
        noRamqButton: "I am not registered to the RAMQ",
        completeBtn: "Fill out",
        okBtn: "OK",
        yearLbl: "Year",
        phoneNumber: "Phone number",
        requiredFieldError: "Required field",
        invalidFormError: "There is/are invalid field(s) in the form",
        selectRequiredFieldError: "You must select an answer",
        ramqNumberInvalidError: "Invalid RAMQ number",
        ramqNotice: {
            question: "No RAMQ card?",
            description: "Note that fees will apply.",
        },
        phoneInvalidError: "Invalid phone number",
        zipCodeInvalidError: "Invalid postal code",
        underFifteenError:
            "Prelib aims its services at everyone aged 15 and older",
        underTwentyFiveError:
            "The HPV test is only available for people born with a uterus and aged 25 or older",
        engageConsent1:
            "By adding your study participant code, you confirm that you have already signed and agreed to the consent form for the Engage 2.0 & MECS study.",
        engageConsent2:
            "By adding your study participant code, you agree that Prelib may share your information with the Engage 2.0 & MECS study, including your personal information, questionnaire responses, and test results.",
        invalidEngageCodeFormat:
            "Enter a valid code in the format MXXXX (e.g., M1234).",
        invalidHpvFlowGender:
            "The HPV test is only available for people born with a uterus.",
    },
    questionnaireSection: {
        completeButtonText: "Fill out",
        header: "Form regarding your general health",
        takeQuestionnaire: "Fill out the questionnaire",
        modal: {
            consentButtonText: "I consent",
            consentHeader: "Consent",
            consentFirstP:
                'By clicking "I accept," I consent to Prelib Technologie Inc. ("Prelib") and its healthcare professionals conducting one or more consultations as part of asymptomatic sexual health screening via the standardized electronic questionnaire.',
            consentSecondP:
                "I attest that I am physically located in Québec, a condition required for this service.",
            consentAcknowledgementHeader: "I acknowledge that:",
            consentAcknowledgements: [
                "Prelib implements the technological measures necessary to protect the confidentiality of my information transmitted electronically;",
                "Telemedicine carries technological risks and limitations;",
                "This site must not be used in the event of a medical emergency;",
            ],
            consentPrivacyPolicyAcknowledgementPrefix:
                "My personal and medical information may be collected, used, and shared in accordance with the ",
            consentThirdP:
                "I authorize the creation of a confidential electronic patient record compiling the information provided during the consultation(s).",
            consentFourthP:
                "I accept this agreement, retain the services of Prelib, and consent to care consultations.",
            continueButtonText: "Continue",
            recommendationHeader: "Recommendations",
            recommendationSubHeader:
                "Prelib strongly recommends you consult a physician in the following situations",
            recommendationFirstP: "Presence of STBBI symptoms",
            recommendationSecondP:
                "Potential exposure during at-risk sexual relations over the past 72 hours to evaluate whether post-exposure prophylaxis (PEP) treatment is suitable for you.",
            recommendationThirdP: "Pregnancy",
            refuseButtonText: "I refuse",
            refusalTitle:
                "Without your consent we can't continue the online screening.",
            refusalBody:
                "The medical questionnaire is a key part of the assessment process. It is a required step that allows our healthcare professionals to assess your situation and prescribe the appropriate tests.",
            refusalWhatHeading: "What that means",
            refusalItems: [
                "Your screening stops here; no appointment is booked.",
                "Your profile details are saved, but the medical questionnaire isn't started.",
                "Your account stays active: you can pick this up any time without redoing your sign-up.",
            ],
            refusalFaqHeading: "Maybe this is what's holding you back",
            refusalFaq: [
                {
                    question: "Who sees my answers?",
                    answer:
                        "Only the Prelib clinical team handling your screening — physician, clinical nurse. Nothing is shared with an employer, an insurer or any third party.",
                },
                {
                    question: "I'm not in Québec right now",
                    answer:
                        "The service is limited to people physically located in Québec at the time of the consultation. Come back to your screening when you return — your account is waiting.",
                },
                {
                    question: "I just want a test, not a consultation",
                    answer:
                        "The questionnaire determines which samples are useful for you and lets us order the lab work. There is no Prelib screening without this step.",
                },
            ],
            refusalBackButtonText: "Read the consent again",
            refusalExitButtonText: "Leave the screening",
            refusalHelpLinkText: "A question? Write to us",
            refusalExitTitle: "Leave the screening?",
            refusalExitBody:
                "Your profile is saved, but no questionnaire is submitted. You go back to home and can pick this up any time.",
            refusalExitConfirmButtonText: "Yes, leave",
            refusalExitCancelButtonText: "Cancel",
        },
    },
    hpvTreatmentDisclaimer: {
        title: "Treatment Terms",
        items: [
            {
                emphasis: "Vaginal self-sampling",
                text: " using a kit, in complete confidentiality.",
            },
            {
                emphasis: "No speculum or pelvic exam.",
                text: " This is not a gynecological exam.",
            },
            {
                emphasis: "A positive result ≠ cancer.",
                text:
                    " If a high-risk HPV is detected, an additional test (cytology or consultation) may be recommended.",
            },
        ],
        positiveResult: {
            emphasis: "If the test is positive",
            text:
                ", a cervical sample using a speculum will be necessary. We'll refer you to a partner clinic.",
        },
        understoodButtonText: "I understand",
    },
    datetimePicker: {
        chooseAnHour: "Choose an hour",
        timePickerPlaceHolder: "Hour",
    },
    bookingFlowConfirmation: {
        title: "Your appointment is confirmed",
        results: "Results",
        entranceCodePrompt: "To enter the clinic, use the code",
        testsToday: "Your tests today",
        howToFindUs: "How to find us",
        beCareful: "Be careful:",
        addToCalendar: "Add to calendar",
        backToHomepage: "Back to homepage",
        reducedMobilityBeforeEmail:
            "If you have reduced mobility, email us at ",
        reducedMobilityAfterEmail:
            " — we'll make sure to help you access the clinic.",
        clinicPhotoAlt: "Clinic entrance",
    },
    privateServiceSection: {
        title: "Would you like to receive your results within 48 hours?",
        subtitle:
            "Choose the private laboratory to receive your results more quickly.",
        total: "Total:",
        forTests: (count: number) => `for the ${count} tests`,
        payableInClinic: "(Payable at the  clinic)",
        yesFastResults: "Yes, fast results",
        continueWithoutChanges: "Continue without changes",
        deliveryNote: "*Average delivery times, not guaranteed",
        appointment: "Appointment",
        results: "Results",
        confirmation: {
            title: "Appointment confirmed!",
            subtitle: "A copy of these details was also sent to your email.",
            privateLab: "(private lab)",
            googleCalendar: "Google Calendar",
            appleCalendar: "Apple Cal",
            icsFileTitle: "Prelib Appointment",
            reminder: {
                title: "Don't forget",
                ramqCard: {
                    main: "Bring your RAMQ card.",
                    note:
                        "Not registered with RAMQ? Bring a piece of photo ID.",
                },
                paymentMethod: {
                    main: "Bring a payment method.",
                    note: "We accept credit, debit and cash.",
                },
                cancellationInfo: {
                    main: "Be right on time!",
                    note:
                        "Cancel or reschedule for free at least 4 hours in advance online. Otherwise, a $35 fee applies.",
                },
            },
        },
    },
    hpvConfirmation: {
        title: "Appointment confirmed!",
        subtitle: "A copy of these details was also sent to your email.",
        appointment: "Appointment",
        googleCalendar: "Google Calendar",
        appleCalendar: "Apple Cal",
        icsFileTitle: "Prelib Appointment",
        reminder: {
            title: "Don't forget:",
            intravaginalProducts: {
                title: "Avoid intravaginal products",
                content:
                    "and douching in the 24 h before the sample. External washing only.",
            },
            heavyPeriod: {
                title: "Reschedule if your period is heavy.",
                content: "The sample could be rejected.",
            },
            cancellation: {
                title: "Cancel at least 4 h ahead",
                content: "to avoid a $35 fee.",
            },
        },
    },
    recommendationsSection: {
        restartQuestionnaire: "Fill out the medical questionnaire once more.",
        analScreening: "Anal screening",
        bloodScreening: "Blood screening",
        customRecommendationsPresentation:
            "Here are some recommendations based on the answers you provided:",
        condomRecommendation:
            "The use of a condom during any sexual relation significantly reduces your risks of contracting an STI.",
        drugSharingRecommendation:
            "If you consume drugs, never share or reuse disposable injection or inhalation instruments.",
        extraCharges:
            "*Note that fees related to your tests will be charged on site.",
        header: "Recommendations",
        importantNewEvaluation:
            "You have 45 days to book an appointment, after which a new medical evaluation will be required.",
        importantCancelTest:
            "Payment for transportation fees ($5 - $15) or Dynacare private laboratory fees, if applicable, will be required at the time of your appointment.",
        importantDisabled:
            "If you have reduced mobility, email us at info@prelib.com — we'll make sure to help you access the clinic.",
        importantFee:
            "At the time of your appointment, you will have the possibility to remove any unwanted laboratory tests.",
        importantHeader: "IMPORTANT",
        importantSymptoms:
            "If you notice any symptoms prior to your appointment, we recommend you seek treatment with a physician.",
        medicalTestsPresentation:
            "Based on the answers you provided, we recommend you complete the tests listed below. You can always modify the selection.",
        minimalTestsCountError: "You must select at least one test.",
        oralScreening: "Oral screening",
        ppeLink: "post-exposure prophylaxis (PEP).",
        ppeRecommendation:
            "Because you had at-risk sexual relations in the past 72 hours, Prelib strongly recommends that you consult a clinic to evaluate whether you should be prescribed",
        prepLink: "pre-exposure prophylaxis (PrEP).",
        prepRecommendation:
            "Since your sexual practices pose a potentially high risk of contracting HIV, visit a specialized clinic and ask about",
        prepRecommendation2: "",
        retakeRecommendationExplanation:
            "Repeated screening tests for the infections listed above will allow to cover the window period, which is the maximum period before the infection can be detected in someone’s blood. ",
        retakeRecommendationHeader:
            "To ensure accurate results, you may be required to redo certain tests, depending on the time elapsed between potential exposure and screening.",
        retakeRecommendationHIV: "HIV: control to be carried out starting",
        retakeRecommendationHepatiteC:
            "Hepatitis C: control to be carried out starting",
        retakeRecommendationSyphilis:
            "Syphilis: control to be carried out starting",
        urinaryScreening: "Urinary screening",
        vaxRecommendation:
            "Your vaccination profile appears to be out of date. Ask your family doctor or a specialized clinic for more information. It’s simple, and often free of charge!",
        combinedOralNaatTest: "Chlamydia/Gonorrhea throat (NAAT)",
        itssSlug: "stbbi",
        appointmentUpdated: "Your appointment has been updated",
        continue: "Continue",
        hpv: {
            recommendation: "Recommendation",
            eligible: {
                title: "Recommendation",
                cardTitle: "You're eligible for this test",
                cardDescription:
                    "Based on your answers to the medical questionnaire.",
                sectionTitle: "Good to know about the test",
                negativeResultTitle: "Negative result: ",
                positiveResultTitle: "Positive result: ",
                positiveResultText:
                    "a cervical sample with a gynecological exam will be necessary.",
                inconclusiveResultTitle: "Inconclusive test: ",
                inconclusiveResultText: "it will need to be repeated.",
                ctaLabel: "Continue",
                eligible: {
                    negativeResultText: "next screening in 5 years.",
                },
                eligible_immunosuppressed: {
                    negativeResultText:
                        "your next screening will be sooner and will be determined with a healthcare professional.",
                },
            },
            outcome: {
                shared: {
                    whyThisResultTitle: "Why this result?",
                    whatToDoNowTitle: "What to do now",
                    ctaLabel: "Back to home",
                },
                consult_symptoms: {
                    cardTitle: "A medical consultation is recommended",
                    cardDescription:
                        "Based on your answers to the medical questionnaire.",
                    whyThisResult:
                        "You reported one or more symptoms. HPV screening is meant for people without symptoms, a negative result wouldn't be enough to rule out a problem.",
                    whatToDoNow:
                        "Book an appointment with a healthcare professional to have your symptoms assessed.",
                },
                consult_followup: {
                    cardTitle: "Continue with your current follow-up",
                    cardDescription:
                        "Based on your answers to the medical questionnaire.",
                    whyThisResult:
                        "You indicated an ongoing treatment or follow-up for your cervix.",
                    whatToDoNow:
                        "Continue with the professional managing your follow-up. Routine screening doesn't replace it.",
                },
                consult_clarify: {
                    cardTitle: "A prior consultation is recommended",
                    cardDescription:
                        "Based on your answers to the medical questionnaire.",
                    whyThisResult:
                        "Some of your answers need to be clarified before screening.",
                    whatToDoNow:
                        "Check your medical record or speak with a healthcare professional to clarify the situation.",
                },
                not_recommended_no_cervix: {
                    cardTitle: "HPV screening isn't recommended for you",
                    cardDescription:
                        "Based on your answers to the medical questionnaire.",
                    whyThisResult:
                        "You indicated you've had a total hysterectomy.",
                    whatToDoNow:
                        "You don't need this screening. If your hysterectomy was related to cervical cancer or a lesion, talk to your doctor.",
                },
                not_recommended_under_25: {
                    cardDescription:
                        "Based on your answers to the medical questionnaire.",
                    cardTitle: "HPV screening isn't recommended for you",
                    whyThisResult: "You're under 25.",
                    whatToDoNow:
                        "You'll be able to do your first screening starting at 25. If you have unusual symptoms, see a doctor.",
                },
                not_recommended_recent_hpv_test: {
                    cardTitle: "You're already up to date",
                    cardDescription:
                        "Based on your answers, you don't need screening right now.",
                    whyThisResult:
                        "You had an HPV test less than 5 years ago. In Québec, the HPV test is done every 5 years.",
                    whatToDoNow:
                        "Your next screening will be 5 years after your last test. If you have unusual symptoms before then, see a doctor.",
                },
                not_recommended_recent_pap_test: {
                    cardTitle: "You're already up to date",
                    cardDescription:
                        "Based on your answers, you don't need screening right now.",
                    whyThisResult:
                        "You had a Pap test less than 3 years ago. It's recommended to wait 3 years after a normal Pap test before moving to the HPV test.",
                    whatToDoNow:
                        "Your next screening will be 3 years after your last Pap test. If you have unusual symptoms before then, see a doctor.",
                },
            },
            bookedAppointment: {
                positive: {
                    highlight: "Positive ≠ cancer.",
                    description:
                        "If a high risk HPV is detected, a cervical sample with a gynecological exam will be required. We'll refer you to a partner clinic.",
                },
                negative: {
                    highlight: "Negative:",
                    description:
                        "next screening in 5 years (sooner if your immune system is weakened).",
                },
                inconclusive: {
                    highlight: "Inconclusive:",
                    description: "the test will need to be repeated.",
                },
                symptoms:
                    "If you have symptoms (unusual bleeding, pain, abnormal discharge), see a professional right away.",
            },
        },
    },
    sectionSelection: {
        appointmentsSection: {
            canceledAppointments: "Your cancelled appointments",
            header: "Your appointments",
            pastAppointments: "Your previous appointments",
            upcomingAppointments: "Your upcoming appointments",
            missedAppointments: "Your missed appointments",
        },
        makeScreeningSection: {
            date: "Appointment",
            information: "Information",
            medicalEvaluation: "Medical evaluation",
            payment: "Payment",
            privateService: "Private service",
            recommendations: "Recommendations",
            expired: "Expired questionnaire",
        },
        myProfileSection: {
            generalHealth: "General health",
            header: "My profile",
            personalInformation: "Personal information",
            sexualHealth: "Sexual health",
        },
        myResultsSection: {
            header: "Your results",
            tooltipMessage: "You have no results.",
        },
        welcomeTextInfo: "Your last screening was completed on",
    },
    resultsSection: {
        invalidResultLabel: "Invalid",
        undisclosedResultLabel: "Pending",
        toRetakeResultLabel: "To be taken again",
        negativeResultLabel: "Negative",
        positiveResultLabel: "Positive",
        notDoneResultLabel: "Not completed",
        cancelled: "Cancelled",
        recommendations: "Recommendations:",
        recommendationsWhenCancel:
            "Even if your appointment has been cancelled, the following recommendations still stand:",
        recommendationsWhenNoResult:
            "Until then, keep the following recommendations in mind:",
        recommendationsWhenNotDone:
            "Even if the appointment is not done yet, those recommendations are still valid:",
        resultsNotReadyLine1:
            "We’re still waiting on your results; it shouldn’t take much longer!",
        resultsNotReadyLine2:
            "You’ll receive a message when your results are available.",
        giveUsYourFeedback: "Got some feedback for us?",
        thankYouForFeedback: "Thank you for your feedback!",
        rowHeader: (date) => `${date} appointment`,
        pendingInfo: {
            header: "Results pending:",
            explanation:
                "A sample is “pending” when results have yet to come back from laboratory analysis. This turnaround time is normal, and the results will be sent to you before long (expect an additional 7 working days).",
        },
        retakeInfo: {
            row1_1: "One of your samples could not be interpreted by the lab.",
            row1_2:
                "This may be a laboratory error or an improper sample. Please don't be alarmed.",
            row2_1:
                "Prelib offers you to retake this sample free of charge provided you make an appointment at our center within the next 45 days.",
            retakeButton: "Reschedule appointment",
            rejectRetakeButton: "You do not want to reschedule an appointment.",
        },
        schedulePhoneAppointment: {
            title:
                "You have been invited to schedule a phone appointment. Please pick a date that suits you",
            scheduleButton: "Schedule appointment",
        },
    },
    resetPassword: {
        changeButtonText: "Change",
        confirmationLabel: "Confirm password",
        header: "New password",
        invalidPasswordError: "Invalid password",
        passwordDontMatchError: "Passwords don't match.",
        invalidTokenError: "The token is invalid!",
        passwordLabel: "Password",
        successMessage: "Password saved. You can now log in.",
    },
    signUp: {
        accountCreated: "Your file was created",
        alreadyHasAccount: "Already have an account?",
        byEmail: "By email",
        bySms: "By SMS",
        confirmationResent: "A confirmation email was just sent to ",
        consentMessage: `I agree to the <a href="https://prelib.com/en/terms" rel="noopener noreferrer" target="_blank">terms of use</a>.`,
        emailConfirmationMessage: "You must now confirm your email address.",
        emailLabel: "Email",
        firstNameLabel: "First name",
        formErrors: "There are errors in the form",
        clickHere: "Click here to",
        header: "Create Your Prelib Account",
        howDidYouHearAboutUs: {
            title: "How did you hear about Prelib?",
            options: {
                search_engine: "Search Engine (Google, Bing, etc.)",
                social_media:
                    "Social Media (Facebook, Instagram, Twitter, etc.)",
                recommendation: "Friend or Family Recommendation",
                online_ads: "Online Ads",
                news_article: "News Article",
                other: "Other",
            },
            pleaseSpecify: "Please specify",
            selectOption: "Select an option...",
        },
        hotmailWarning:
            "If you use @hotmail, Prelib emails may end up in your spam folder. We recommend that you add Prelib to your contact list so you don't miss any emails.",
        invalidEmailError: "Invalid email address",
        invalidPasswordError: "Invalid password",
        login: "Login",
        loginToAccount: "login",
        lastNameLabel: "Last name",
        nameWarning: "As shown on your RAMQ card",
        optionalField: "*optional",
        passwordLabel: "Password",
        passwordWarning:
            "Your password is strictly confidential and must not be disclosed to third parties.",
        phoneNumberError: "Invalid phone number",
        phoneNumberLabel: "Phone number",
        phoneNumberPlaceholder: "(xxx) xxx-xxxx",
        requiredFieldError: "Required field",
        resend: "Resend confirmation email",
        rateLimitWaitMessage:
            "Please wait before requesting another confirmation email.",
        signUpButtonText: "Register",
        tfaLabel: "Enable 2-Step Verification",
        tfaDisclaimer:
            "I agree to get 2FA texts from Prelib. Message frequency varies. Message and data rates may apply. Reply STOP anytime.",
        warning: "Warning",
    },
    tfaInput: {
        title: "2-Step Authentication",
        sentTo: (sentBy, sentTo) =>
            `Your account is protected by 2-Step authentication. A 6-digit authentication code has been sent by ${sentBy} to ${sentTo}.`,
        resentCode: (sentBy, sentTo) =>
            `A 6-digit authentication code has been resent by ${sentBy} to ${sentTo}.`,
        checkboxLabel:
            "Don't ask me for the code again for 30 days when I use this device.",
        fieldLabel: "Enter 6-digit code :",
        codeError: "Invalid code",
        verifyButton: "Authenticate",
        verifySpanBoxTitle: "Did you check your spam folder?",
        verifySpanBoxSubTitle: "The email is sent from info@prelib.com",
        resendCode: "Send a new code",
        sendNewCodeSuccess: "A new code has been sent",
        sendNewCodeError: "Error while sending new code",
        rateLimitWaitMessage:
            "Please wait before requesting another authentication code.",
    },
    tfaSettings: {
        title: "2 step-verification",
        subtitle:
            "For the security of your data, your account will now be protected by 2-step authentication. When logging in, you will have to confirm your identity using a code. How do you want to receive this code?",
        confirmButton: "Continue",
        bySms: "By SMS",
        byEmail: "By email",
        phoneConfirmationButton: "Confirm phone number",
        phoneNumberLabel: "Phone number",
        phoneNumberPlaceholder: "Phone number format: (xxx) xxx-xxxx",
        phoneNumberError: "Invalid phone number",
    },
    select: {
        startTyping: "Start typing",
        keepTyping: "Keep typing",
        noResults: "No results",
    },
    months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ],
    humanizeDateFormatter: {
        unknownDate: "No time slot are available at the moment",
        nextAvailableTime: "The next available time slot is",
        date: (day, shortMonth, hour) => `${shortMonth} ${day} at ${hour}`,
        nextAvailability: "From",
    },
    addressForm: {
        addressLine: "Postal address",
        city: "City",
        province: "Province",
        country: "Country",
        postalCode: "Postal Code",
    },
    prepSection: {
        prepStatus: {
            title: "PrEP status",
            active: {
                title: "PrEP Status: Active",
                description:
                    "When PrEP status is active, this means you are taking pre-exposure prophylaxis (PrEP) via PrEP Solution.",
                btn: "Stop PrEP monitoring",
                history: "My History",
                today: "Today",
                by: "by",
                expiration: "Expiration date of",
                status: {
                    ELIGIBLE: "PrEP status: Active",
                    PENDING: "PrEP status: Pending",
                    INELIGIBLE: "PrEP status: Inactive",
                    REVOKED: "PrEP status: Inactive",
                    EXPIRED: "PrEP status: Inactive",
                },
            },
            suspended: {
                title: "PrEP Solution: Access suspended for new patients",
                description: "A new solution is coming: stay tuned!",
                existingPatient: {
                    title: "Already a PrEP Solution patient? ",
                    text: "No change for you, the service remains available.",
                },
            },
        },
        prepDelivery: {
            title: "Delivery Profile",
            disabledPopover: "PrEP status required",
            inactive: {
                title: "Delivery: inactive",
                description:
                    "Shipping is currently inactive for your account. To activate and select your delivery profile, choose the delivery option that suits you. Your request will then be examined and confirmed by the participating pharmacy.",
            },
            active: {
                title: {
                    "1_MONTH": "My current plan: Scheduled Delivery (1 month)",
                    "3_MONTHS":
                        "My current plan: Scheduled Delivery (3 months)",
                    ON_DEMAND: "My current plan: On-Demand Delivery",
                    ON_PAUSE: "Delivery on Pause",
                },
                nextDeliveryTitle: "My next delivery",
                pastDeliveriesTitle: "Past deliveries",
                futureDeliveriesTitle: "Future deliveries",
                actionTooltip: {
                    activateDeliveryToEdit: "Activate delivery to edit",
                    editDate: "Edit date",
                    editDestination: "Edit destination",
                    activate: "Activate",
                    cancel: "Cancel",
                },
                tabHeader: {
                    deliveryDate: "Delivery Date",
                    status: "Status",
                    address: "Address",
                    actions: "Actions",
                },
                editDate: {
                    title: "Choose your new delivery date",
                    description:
                        "Select a new delivery date from the calendar below.",
                    continue: "Continue",
                    reject: "Cancel",
                },
                editAddress: {
                    title: "Choose your delivery method",
                    destinationSelect: {
                        atHome: "At home",
                        postPickUp: "Post pick-up",
                        pharmacyPickUp: "Pharmacy pick-up",
                        prelibPickUp: "Prelib pick-up",
                    },
                    continue: "Continue",
                    reject: "Cancel",
                    adresse: "Address",
                    city: "City",
                    zipCode: "Zip code",
                    province: "Province",
                    applyToAll:
                        "I would like to apply this change to all future deliveries",
                    pharmacyPickUp: "Delivery to the Pharmacie du Village",
                    prelibPickUp: "Pick-up at the Prelib center: ",
                    on: "on",
                    atNextAppointment: " at your next screening appointment.",
                    validationMessages: {
                        address: "Please indicate your address!",
                        city: "Please indicate your city!",
                        zipCode: "Please indicate your zip code!",
                    },
                },
                cancelDelivery: {
                    title: "Cancellation of delivery for the current month",
                    description:
                        "If you refuse a scheduled delivery, you can always reactivate it before your next one. Once a delivery has been confirmed, you can cancel it up to 72 hours before the scheduled date.",
                    cancel: "Cancel my delivery",
                },
                deliverySummary: {
                    quantityLabel: "Quantity: ",
                    destinationLabel: "Delivery destination: ",
                    addressLabel: "Delivery address: ",
                    justificationLabel: "Pharmacist's message: ",
                    quantity: { "1_MONTH": "1 month", "3_MONTHS": "3 months" },
                    destination: {
                        RESIDENCE: "At home",
                        POST_PICK_UP: "Pickup (Canada Post)*",
                        PHARMACY_PICK_UP: "Pick-up at Pharmacie du Village",
                        PRELIB_PICK_UP: "Pick-up (Prelib)",
                    },
                    pendingDeliveryDescription:
                        "Your delivery status is pending. The delivery will be confirmed by the pharmacist in the week preceding the delivery.",
                    confirmedAt: "Delivery confirmed on:",
                    rejectedAt: "Delivery rejected by the pharmacist on: ",
                    deliveredAt: "Delivery delivered on: ",
                    prelibPickUpDisclaimer: `*The delivery date is the date of your next Prelib screening appointment at Prelib. If your appointment is cancelled, your PrEP will be delivered by the alternative method of your preference.`,
                    modify: "Modify",
                    confirm: "Confirm",
                    refuse: "Refuse",
                    pickUpAtPrelibOn: "Pick-up during the screening on ",
                    pharmacieDuVillage: "Pharmacie du Village",
                    prelibPickUpInfo1:
                        "If you confirm a delivery but change your mind, you can always cancel it up to 72 hours before the scheduled date.",
                    prelibPickUpInfo2:
                        "If you refuse a scheduled delivery, you can reactivate it before your next one.",
                    der: {
                        REJECTED:
                            "The date change for this delivery has been rejected, the delivery will remain on the original date.",
                        ACCEPTED:
                            "The date change for this delivery has been accepted, the delivery will be delivered on the new date.",
                    },
                    requestedDate: "Requested date: ",
                },
                showMore: "show more",
                showLess: "show less",
                description: {
                    "1_MONTH":
                        'The "Scheduled delivery (1 month)" profile allows you to benefit from automatic PrEP delivery every month, provided you keep your medical records up to date. This option can give you greater flexibility in managing your PrEP, avoiding oversights and repeated trips.',
                    "3_MONTHS":
                        'The "Scheduled delivery (3 months)"" profile allows you to benefit from automatic PrEP delivery every 3 months, provided you keep your medical records up to date. This option can give you greater flexibility in managing your PrEP, avoiding oversights and repeated trips.',
                    ON_DEMAND:
                        'The "On-Demand Delivery" profile allows you to benefit from a PrEP delivery whenever you wish, without using the automated scheduling tool.',
                    ON_PAUSE: "",
                },
                onDemand: {
                    quantityLabel: "Desired quantity*:",
                    dateLabel: "Delivery date:",
                    datePlaceholder: "Select a date",
                    quantityError: "Please choose a quantity!",
                    dateError: "Please choose a delivery date!",
                    submitBtnLabel: "Request Delivery",
                    quantityOptions: {
                        "1_MONTH": "1 month",
                        "3_MONTHS": "3 months",
                    },
                    quantityDisclaimer: "",
                },
                status: {
                    REQUEST_IN_PROGRESS: "On request",
                    PENDING: "Pending",
                    CANCELED: "Canceled",
                    CONFIRMED: "Confirmed",
                    DELIVERED: "Delivered",
                    REJECTED: "Rejected",
                },
                btn: "TODO",
                preferencesCTA: "Preferences",
            },
        },
        deliverySettingsForm: {
            title: "My delivery settings",
            subtitles: [
                "Fill out your delivery preferences below.",
                "You can adjust them later.",
            ],
            date: {
                label: "Delivery date",
                placeholder: "Delivery date selection",
                warning:
                    "*You will not be able to select a delivery option for the next 72 hours, while the pharmacist confirms your request.",
            },
            frequency: {
                label: "Delivery frequency",
                placeholder: "Delivery frequency selection",
                options: {
                    "1_MONTH": {
                        title: "Recurring (every month)",
                    },
                    "3_MONTHS": {
                        title: "Recurring (every 3 months)",
                    },
                    ON_DEMAND: {
                        title: "On demand*",
                        details:
                            "* Cette option te permet de demander une livraison lorsque tu le souhaites sans utiliser une planification automatisée.",
                    },
                    ON_PAUSE: {
                        title: "Paused*",
                        details:
                            "* Lorsque tu mets sur pause tes livraisons, ton statut PrEP est toujours actif. Si tu le souhaites, tu peux réactiver tes livraisons via ton profil de préférence.",
                    },
                },
            },
            destination: {
                label: "Delivery destination",
                placeholder: "Delivery destination selection",
                options: {
                    RESIDENCE: {
                        title: "At home",
                    },
                    POST_PICK_UP: {
                        title: "Pickup (Canada Post)*",
                        details:
                            "* If you select Canada Post pick-up, a delivery notice will be sent to the email address in your file when your package is ready for pick-up at a point of service near you.",
                    },
                    PHARMACY_PICK_UP: {
                        title: "Pick-up at Pharmacie du Village",
                    },
                },
            },
            address: {
                label: "Address",
                placeholder: "ex: 1050 Lorem Ipsum",
            },
            city: {
                label: "City",
                placeholder: "ex: Montreal",
            },
            zipCode: {
                label: "Postal Code",
                placeholder: "ex: H3B0G3",
            },
            province: {
                label: "Province",
                value: "QC",
                tooltip: "Delivery available only in Quebec",
            },
            activateDeliveryCTA: "Enable deliveries",
            saveDeliverySettingsCTA: "Save",
            cancelDeliverySettingsCTA: "Cancel",
        },
    },
    clinicSelection: {
        all: "All",
        privateLaboratory: {
            resultsIn48Hours: "Results in 48 hours",
            disclaimer:
                "For on-site testing, you understand that public laboratory analysis covered by the RAMQ is not offered at the clinic and accept the private laboratory fees.",
        },
        cityList: {
            ariaLabel: "Filter clinics by city",
        },
        loadMoreClinics: "Load more clinics",
        otherClinics: {
            title: "The other clinics",
            description: "Closest to farthest",
        },
    },
    privateLaboratoryBanner: {
        title: "Private laboratory only",
        waitTime: {
            results: "results",
            quick: "Quick",
            in: "within",
            time: "24 to 72 business hours",
        },
        cost: {
            lowRange: "$150",
            to: "to",
            highRange: "$375",
            location: "payable at the clinic",
            description: "(depending on the tests required)",
        },
    },
    breadcrumb: {
        title: "Progress",
        step: "Step",
    },
    shareBlock: {
        pill: "Share it",
        title: "Spread the word",
        description:
            "If Prelib made screening simpler for you, share it with someone who could benefit — a friend, a match or simply someone you care about!",
        smsLabel: "Share by text message",
        whatsAppLabel: "Share on WhatsApp",
        emailLabel: "Share by email",
        copyLabel: "Copy link",
        linkLabel: "Link to share",
        linkCopied: "Link copied!",
        manualCopy: "The link is selected, copy it to share.",
        smsDesktopNote:
            "Text messages are only available on mobile. Copy the link below instead.",
    },
} as Translations;
